// ── The identity wall's wires ────────────────────────────────────────────────
// What the right upright reads. Four questions about the grant between this
// person and Integrations, each answered by Paradigm directly.
//
// NO NEW BACKEND ROUTES. index.js already proxies /api/* to Paradigm with this
// app's key and the caller's user id, so these are the platform's own endpoints
// reached through the door that already exists. Ink wrote three named handlers
// for the same three calls; here they would be three copies of the proxy.
//
// NO NEW GRANT EITHER, which is the non-obvious part and worth writing down:
//
//   /my-permissions            introspection. It reports the boundary; it does
//                              not read across it, so it is not gated by the
//                              boundary it describes.
//   /third-party/my-activity   Integrations' OWN receipts. Every row is
//                              something this app did, so it discloses nothing
//                              this app did not already hold — deliberately
//                              exempt from `activity:read` for that reason.
//   /nodes                     EP-scoped by construction. The platform decides
//                              what comes back, so this cannot widen anything.
//
// (Ink's server.js says the same in its own words above the same three routes.)

async function get(path) {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

/**
 * The live EP ∩ Realm, including the pollable `reauth` signal.
 *
 * Returns null rather than throwing: the wall renders "no active authorization"
 * for null, which is the honest reading of a call that did not come back. A
 * thrown error here would take the whole chrome down with it.
 */
export async function loadPermissions() {
  try {
    return await get('/api/my-permissions');
  } catch {
    return null;
  }
}

/** The ledger — what Integrations and its automations did, newest first. */
export function loadActivity(params = {}) {
  const q = new URLSearchParams();
  for (const k of ['page', 'per_page', 'search', 'categories']) {
    if (params[k]) q.set(k, params[k]);
  }
  return get(`/api/third-party/my-activity?${q}`);
}

/** The library — everything inside the grant, whoever wrote it. */
export function loadLibrary(params = {}) {
  const q = new URLSearchParams();
  for (const k of ['limit', 'offset', 'search', 'schema_id']) {
    if (params[k]) q.set(k, params[k]);
  }
  return get(`/api/nodes?${q}`);
}

/**
 * The schemas the grant actually exposes, for the Library's facet.
 *
 * Derived from the permissions payload rather than fetched separately: the facet
 * must offer exactly what the person allowed, and a second source could offer a
 * schema the grant does not cover.
 */
export function schemasFrom(permissions) {
  if (!permissions?.effective_verbs?.nodes) return [];
  const ids = permissions.selected_schema_ids || permissions.schema_ids || [];
  return ids.map((id) => ({ id, name: id }));
}
